// Works out what creatures deal and resist, and what items add and protect against,
// so loot patterns can pair them up (see loot_patterns).
//
// Creatures come from their light copies (after Refresh Light Data, those know the damage types they deal).
// Items and magic item keywords have to be loaded in full, since their light copies can't tell
// damage they add apart from damage they protect against. That's slow, so it's done once per session.

import {
  Ability,
  Creature,
  CreatureLimitation,
  DamageMultiplier,
  ExtraEffectsFeature,
  ExtraEffectsOfTypeFeature,
  LootEntry,
  LootItemGroup,
  LootPattern,
  ItemLimitation,
  PseudoItem,
  WeaponDamageUpgradeFeature,
} from "@dndModels";
import { FeatureSource } from "@models";
import { TRUE_DAMAGE_TYPES } from "@dndModels/Constants";
import { DamageType, isDamageType } from "@dndModels/Types";

import DandDAPI from "@utilities/smart_api";
import DataUtilities from "@dndUtilities/data_utilities";
import { loadEditorItems } from "@dndUtilities/encounter_loot";
import { CreatureFacts, ItemFacts, PlannedEntry } from "@dndUtilities/loot_patterns";

const api = DandDAPI.getInstance();
const data_util = DataUtilities.getInstance();

export interface LootAnalysis {
  creatures: { facts: Omit<CreatureFacts, "fits_extra">, creature: Creature }[];
  items: ItemFacts[];
}

interface DamageFacts {
  adds: Set<DamageType>;
  resists: Set<DamageType>;
  adds_extra: boolean; // Adds whatever damage type is chosen for it (like Armor of Resistance's choice)
  resists_extra: boolean;
}

const emptyFacts = (): DamageFacts => ({ adds: new Set(), resists: new Set(), adds_extra: false, resists_extra: false });

const hasFacts = (facts: DamageFacts) =>
  facts.adds.size > 0 || facts.resists.size > 0 || facts.adds_extra || facts.resists_extra;

// Goes through a fully loaded item or keyword's features.
const damageFacts = (source: FeatureSource): DamageFacts => {
  const facts = emptyFacts();
  for (const fb of source.feature_bases) {
    for (const feature of fb.features) {
      const ff = feature.floating_feature;
      if (ff instanceof DamageMultiplier) {
        // 0 is immunity, 0.5 is resistance (2 would be vulnerability, which isn't protection)
        if (ff.multiplier === 0 || ff.multiplier === 0.5) {
          for (const dt of ff.the_damage_types) {
            if (dt === "Use Extra") {
              facts.resists_extra = true;
            } else if (isDamageType(dt)) {
              facts.resists.add(dt);
            }
          }
        }
      } else if (ff instanceof Ability || ff instanceof ExtraEffectsFeature || ff instanceof ExtraEffectsOfTypeFeature) {
        for (const effect of ff.effects) {
          for (const dt of effect.damage_types as string[]) {
            if (dt === "Use Extra") {
              facts.adds_extra = true;
            } else if (isDamageType(dt)) {
              facts.adds.add(dt);
            }
          }
        }
      } else if (ff instanceof WeaponDamageUpgradeFeature) {
        const dt = ff.damage_rolls.type as string;
        if (isDamageType(dt)) {
          facts.adds.add(dt);
        }
      }
    }
  }
  for (const dt of ["Typeless", "Same as Main"] as DamageType[]) {
    facts.adds.delete(dt);
    facts.resists.delete(dt);
  }
  return facts;
};

// The damage types something that uses a Damage Type extra can be made with.
const extraDamageTypes = (limits: string[]): DamageType[] => {
  const allowed = limits.filter((o): o is DamageType => isDamageType(o));
  return allowed.length > 0 ? allowed : TRUE_DAMAGE_TYPES;
};

const toItemFacts = (pseudo_item: PseudoItem, facts: DamageFacts[], extra: DamageType | ""): ItemFacts => {
  const adds = new Set<string>();
  const resists = new Set<string>();
  for (const f of facts) {
    f.adds.forEach(o => adds.add(o));
    f.resists.forEach(o => resists.add(o));
    if (extra !== "") {
      if (f.adds_extra) adds.add(extra);
      if (f.resists_extra) resists.add(extra);
    }
  }
  return {
    id: pseudo_item.id,
    name: pseudo_item.name,
    item_type: pseudo_item.item_type,
    rarity: pseudo_item.rarity,
    adds: Array.from(adds),
    resists: Array.from(resists),
  };
};

let cached: LootAnalysis | null = null;

export const cachedLootAnalysis = () => cached;

export const analyzeLootData = async (onProgress: (message: string, fraction: number) => void): Promise<LootAnalysis> => {
  onProgress("Loading Items", 0);
  await loadEditorItems();

  onProgress("Loading Creatures", 0);
  const creatures = (await api.getObjects({ data_type: "creature" }) as Creature[]).filter(o => o.is_dominant);

  const keywords = data_util.magic_item_keywords.filter(o => o.min_rarity > -1);
  // Only items with features that might deal or protect against damage are worth loading.
  const items = data_util.items;
  // Once the light copies have been refreshed they know their associated damage types,
  // so only the ones that have some need loading in full. Until then, everything does.
  const refreshed = keywords.some(o => o.associatedDamageTypes.length > 0);
  const keywords_to_load = refreshed ? keywords.filter(o => o.associatedDamageTypes.length > 0) : keywords;
  const items_to_load = refreshed ? 
    items.filter(o => o.associatedDamageTypes.length > 0) :
    items.filter(o => o.feature_types.includes("Damage Multiplier") || o.damage_types.length > 0);
  const total = keywords_to_load.length + items_to_load.length;
  let done = 0;

  const keyword_facts = new Map<string, DamageFacts>();
  for (const keyword of keywords_to_load) {
    onProgress(`Loading Magic Item Keywords (${done} / ${total})`, done / Math.max(total, 1));
    try {
      await api.loadObject(keyword);
      const facts = damageFacts(keyword);
      if (keyword.override_weapon_damage_type !== "" && isDamageType(keyword.override_weapon_damage_type)) {
        facts.adds.add(keyword.override_weapon_damage_type);
      }
      if (hasFacts(facts)) {
        keyword_facts.set(keyword._id, facts);
      }
    } catch (ex) {
      console.error(ex);
    }
    done++;
  }

  const item_facts = new Map<string, DamageFacts>();
  for (const item of items_to_load) {
    onProgress(`Loading Items (${done} / ${total})`, done / Math.max(total, 1));
    try {
      await api.loadObject(item);
      const facts = damageFacts(item);
      if (hasFacts(facts)) {
        item_facts.set(item._id, facts);
      }
    } catch (ex) {
      console.error(ex);
    }
    done++;
  }

  onProgress("Matching Items and Keywords", 1);
  const results: ItemFacts[] = [];
  const keywords_by_id = new Map(keywords.map(o => [o._id, o]));
  for (const item of items) {
    const base_facts = item_facts.get(item._id);
    if (base_facts) {
      const extras = item.extra_base_type === "Damage Type" ? extraDamageTypes(item.extra_base_limits as string[]) : [""];
      for (const extra of extras) {
        results.push(toItemFacts(new PseudoItem(item, extra), [base_facts], extra as DamageType | ""));
      }
    }
    for (const mik_id of item.possible_mik_ids) {
      const keyword = keywords_by_id.get(mik_id);
      const facts = keyword_facts.get(mik_id);
      if (!keyword || !facts) {
        continue;
      }
      const extras: (DamageType | "")[] = keyword.extra_base_type === "Damage Type" ? extraDamageTypes(keyword.extra_base_limits) : [""];
      for (const extra of extras) {
        const pseudo_item = new PseudoItem(item);
        pseudo_item.add_keyword(keyword, extra);
        // Same limit getPseudoItemsForItem uses
        if (pseudo_item.rarity > 4) {
          continue;
        }
        results.push(toItemFacts(pseudo_item, base_facts ? [base_facts, facts] : [facts], extra));
      }
    }
  }

  cached = {
    creatures: creatures.map(o => ({
      facts: {
        id: o._id,
        name: o.name,
        creature_type: o.creature_type,
        cr: o.challenge_rating,
        min_cr: o.min_challenge_rating,
        max_cr: o.max_challenge_rating,
        dealt: o.damage_types_dealt,
        resisted: o.damage_types_resisted,
      },
      creature: o,
    })),
    items: results.filter(o => o.adds.length > 0 || o.resists.length > 0),
  };
  return cached;
};

// The pattern's creature facts, with its other creature rules checked.
export const creatureFactsFor = (analysis: LootAnalysis, pattern: LootPattern): CreatureFacts[] => {
  const limit = pattern.creature_limit;
  const check_extra = !limit.isEmpty();
  return analysis.creatures.map(o => ({
    ...o.facts,
    fits_extra: !check_extra || api.fitCreatureLimit(o.creature, limit),
  }));
};

// Turns what planEntries came up with into loot entries tagged with the pattern.
export const entriesForPattern = (pattern: LootPattern, planned: PlannedEntry[]): LootEntry[] => {
  return planned.map(plan => {
    const entry = new LootEntry();
    entry.name = plan.name;
    entry.weight = plan.weight;
    entry.pattern_id = pattern.true_id;
    // The CR band goes on the creature, so the creature dealing the damage is the one that has to be in it
    const limit: CreatureLimitation = pattern.creature_limit.clone();
    limit.min_challenge_rating = plan.band.min_cr;
    limit.max_challenge_rating = plan.band.max_cr;
    if (pattern.creature_relation === "Deals") {
      limit.damage_types_dealt = [plan.damage_type as DamageType];
    } else {
      limit.damage_types_resisted = [plan.damage_type as DamageType];
    }
    entry.creature_limit = limit;
    const group = new LootItemGroup();
    group.source = "Items";
    group.count = pattern.items_per_drop.clone(false);
    const item_limit = new ItemLimitation();
    item_limit.pseudo_item_ids = [...plan.item_ids];
    group.item_limit = item_limit;
    entry.item_groups = [group];
    return entry;
  });
};

// So the editor can show item names for generated entries without loading everything.
export const analysisItemNames = (analysis: LootAnalysis | null) =>
  new Map((analysis?.items ?? []).map(o => [o.id, o.name]));
